// 覆蓋整個檔案：ui/utils/fileTextExtractor.ts

import * as mammoth from "mammoth";
import { extractTextFromPdf, renderPdfFirstPageAsJpegDataUrl } from "./pdfTextExtractor";

export type ExtractedResult = {
  ok: boolean;
  text: string;
  detectedType: 'txt' | 'docx' | 'csv' | 'pdf' | 'unknown';
  warnings?: string[];
  meta?: { filename: string; size: number; mime?: string };
};

const MAX_CHARS = 120000;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * 前端檔案解析器：將 File 轉為純文字
 * 用於 Debug 測試與 AI 輔助功能（避免後端/Worker 解析 PDF）
 */
export async function extractPlainTextFromFile(file: File): Promise<ExtractedResult> {
  const meta = {
    filename: file.name,
    size: file.size,
    mime: file.type
  };

  if (file.size > MAX_FILE_SIZE) {
    return {
      ok: false,
      text: "",
      detectedType: 'unknown',
      warnings: ["檔案過大 (超過 10MB)，請先縮小檔案內容。"],
      meta
    };
  }

  const ext = file.name.split('.').pop()?.toLowerCase() || "";
  let text = "";
  let detectedType: ExtractedResult['detectedType'] = 'unknown';
  const warnings: string[] = [];

  try {
    if (ext === 'txt') {
      detectedType = 'txt';
      text = await file.text();
    }
    else if (ext === 'csv') {
      detectedType = 'csv';
      text = await file.text();
    }
    else if (ext === 'docx') {
      detectedType = 'docx';
      const arrayBuffer = await file.arrayBuffer();
      // 使用 mammoth browser version (需在環境中可用)
      const result = await mammoth.extractRawText({ arrayBuffer });
      text = result.value;
      if (result.messages && result.messages.length > 0) {
        warnings.push(...result.messages.map((m: any) => m.message));
      }
    }
    else if (ext === 'pdf' || (file.type || '').includes('pdf')) {
      detectedType = 'pdf';
      // 1) Try normal PDF text-layer extraction
      text = await extractTextFromPdf(file);

      // 2) OCR fallback for scanned PDFs (text layer empty/too short)
      const compactLen = (text || "").replace(/\s+/g, "").length;
      if (compactLen < 40) {
        try {
          const dataUrl = await renderPdfFirstPageAsJpegDataUrl(file, {
            maxWidth: 1600,
            jpegQuality: 0.72,
          });
          const resp = await fetch("/api/ai-ocr", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ imageDataUrl: dataUrl }),
          });
          const json = await resp.json().catch(() => ({}));
          const ocrText = typeof json?.text === "string" ? json.text : "";
          if (resp.ok && ocrText.trim()) {
            text = (text ? text + "\n\n" : "") + "[OCR 首頁]\n" + ocrText.trim();
            warnings.push("偵測到掃描型 PDF：已啟用 OCR（僅辨識首頁）。");
          } else {
            warnings.push("偵測到掃描型 PDF，但 OCR 未取得可用文字（請改用更清晰的掃描檔或改傳可選取文字的 PDF）。");
          }
        } catch (e: any) {
          warnings.push(`偵測到掃描型 PDF，但 OCR 失敗: ${e?.message || String(e)}`);
        }
      }
    }
    else {
      return {
        ok: false,
        text: "",
        detectedType: 'unknown',
        warnings: ["目前僅支援 .txt, .csv, .docx, .pdf 格式。"],
        meta
      };
    }

    // 截斷處理
    if (text.length > MAX_CHARS) {
      text = text.slice(0, MAX_CHARS);
      warnings.push(`內容過長 (超過 ${MAX_CHARS} 字)，已自動截斷。`);
    }

    return {
      ok: true,
      text,
      detectedType,
      warnings,
      meta
    };

  } catch (e: any) {
    console.error("[FileExtractor] Failed", e);
    return {
      ok: false,
      text: "",
      detectedType,
      warnings: [`解析失敗: ${e.message}`],
      meta
    };
  }
}

